import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Filters from './Filters';
import Lots from '../../components/Lots';
import { getPageSpecificFilterByPathname, safeDecode } from '../../helpers/utils';
import { useStores } from 'stores';
import { useQueryParams } from '../../hooks/useQueryParams';
import { MAX_SEARCH_QUERY_LENGTH } from 'constants/common';

const HomePage = ({ title }) => {
  const { userStore, lotStore } = useStores();
  const { lotDataTotal, currentPage, filterLots, setFilters, isLotDataLoading } = lotStore;
  const { userProfile } = userStore;

  const [hideFilters, setHideFilters] = useState(true);

  const { queryParams, updateQueryParams } = useQueryParams();

  const location = useLocation();

  const totalPages = Math.ceil(lotDataTotal / lotStore.lotsPerPage);
  const pages = Array.from({ length: totalPages }).map((_, i) => i + 1);

  const PAGE_SPECIFIC_FILTER = getPageSpecificFilterByPathname(location.pathname);

  const onFilterChange = newValues => {
    const productTypeUpdatedNewValues = newValues.map(item => ({
      ...item,
      value: item.value,
    }));

    const newValuesObject = productTypeUpdatedNewValues.reduce((acc, currentValue) => {
      let valueForFilters = currentValue.value;
      if (currentValue.name === 'query' && typeof valueForFilters === 'string') {
        valueForFilters = safeDecode(valueForFilters);
        valueForFilters = (valueForFilters || '').slice(0, MAX_SEARCH_QUERY_LENGTH);
      }
      acc[currentValue.name] = valueForFilters;

      return acc;
    }, {});

    setFilters(newValuesObject);

    const newValuesForUrl = productTypeUpdatedNewValues.map(item => {
      if (item.name === 'query' && typeof item.value === 'string') {
        const decoded = safeDecode(item.value);
        const truncated = decoded.slice(0, MAX_SEARCH_QUERY_LENGTH);
        return { name: item.name, value: encodeURIComponent(truncated) };
      }
      return item;
    });
    updateQueryParams(newValuesForUrl);
  };

  useEffect(() => {
    const rawQuery = queryParams.query || '';
    const decoded = safeDecode(rawQuery);
    const truncated = decoded.slice(0, MAX_SEARCH_QUERY_LENGTH);
    const encodedTruncated = encodeURIComponent(truncated);

    if (decoded.length > MAX_SEARCH_QUERY_LENGTH) {
      updateQueryParams([{ name: 'query', value: encodedTruncated }]);
      setFilters({ ...queryParams, query: truncated });
      return;
    }

    setFilters({ ...queryParams, query: truncated });
    filterLots(PAGE_SPECIFIC_FILTER);

    return () => {
      lotStore.resetFiltersParams();
    }
  }, [queryParams, userProfile.preferredCurrency]);

  return (
    <main onClick={() => lotStore.dismissToast()}>
      <Filters
        onFilterChange={onFilterChange}
        hideFilters={hideFilters} />
      <Lots
        hideFilters={hideFilters}
        hideFiltersCallback={() => setHideFilters(!hideFilters)}
        isLotDataLoading={isLotDataLoading}
        lotTotal={lotDataTotal}
        pages={pages}
        lotStore={lotStore}
        currentPage={currentPage}
        totalPages={totalPages}
        title={title}
        onFilterChange={onFilterChange}
      />
    </main>
  );
};

HomePage.displayName = 'HomePage';
HomePage.propTypes = {
  lotStore: PropTypes.shape({
    lotDataTotal: PropTypes.number,
    lotData: PropTypes.array,
    currentPage: PropTypes.number,
  }),
  title: PropTypes.string,
};

export default observer(HomePage);
